import I18n from "common/i18n";
import { QuotaStatus, usePlanLimits } from "features/subscription";
import { PLAN_COMPARISON_ITEMS, formatPlanLimit } from "features/user/constants";

const DASHBOARD_USAGE_KEYS = ["projects", "aiChatDaily", "aiImageDaily", "assets"] as const;

const UsageItem = ({
  icon,
  label,
  color,
  format,
  unitKey,
  quota,
}: {
  icon: string;
  label: string;
  color: string;
  format?: "bytes";
  unitKey?: string;
  quota: QuotaStatus;
}) => {
  const percent = quota.unlimited
    ? 100
    : quota.limit > 0
      ? Math.min((quota.used / quota.limit) * 100, 100)
      : 0;
  const unitSuffix = format === "bytes" || !unitKey ? "" : I18n.t(unitKey);
  const usedText =
    format === "bytes" ? formatPlanLimit(quota.used, false, format) : String(quota.used);
  const limitText = quota.unlimited
    ? formatPlanLimit(quota.limit, quota.unlimited, format)
    : formatPlanLimit(quota.limit, false, format);
  const isUnlimited = quota.unlimited;
  const isAccessDenied = quota.accessDenied;

  return (
    <div className="flex-col-el flex-1 min-w-0 items-center gap-1">
      <span className="material-symbols-outlined type-hero shrink-0" style={{ color }}>
        {icon}
      </span>
      <p className="type-text type-body text-textcolor-black leading-tight truncate">{label}</p>
      {isAccessDenied ? (
        <p className="type-text type-body leading-tight whitespace-nowrap" style={{ color }}>
          —
        </p>
      ) : (
        <div className="flex-row-el items-baseline gap-1">
          <p className="type-text type-body leading-tight whitespace-nowrap" style={{ color }}>
            {`${usedText} / ${limitText}`}
          </p>
          {!isUnlimited && unitSuffix && (
            <p className="type-text type-sm leading-tight whitespace-nowrap" style={{ color }}>
              {unitSuffix}
            </p>
          )}
        </div>
      )}
      <div className="flex-col-el w-full h-[10px] rounded-full bg-gray2-40 overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${percent}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

export const DashboardPlanUsage = () => {
  const limits = usePlanLimits();
  const items = DASHBOARD_USAGE_KEYS.map((key) =>
    PLAN_COMPARISON_ITEMS.find((item) => item.key === key),
  ).filter((item): item is (typeof PLAN_COMPARISON_ITEMS)[number] => Boolean(item));

  return (
    <div className="flex-col-el relative rounded-xl bg-white/60 border-2 border-solid border-beige-60 px-4 pt-6 pb-3 w-full">
      <div className="flex-row-el flex-center absolute -top-3 left-4 px-3 py-1 bg-beige-alt rounded-lg">
        <p className="type-text type-sm text-white">{I18n.t("MSG_DASHBOARD_PLAN_USAGE_TITLE")}</p>
      </div>
      <div className="flex-row-el items-stretch w-full gap-3">
        {items.map((item) => (
          <UsageItem
            key={item.key}
            icon={item.icon}
            label={I18n.t(item.labelKey)}
            color={item.color}
            format={item.format}
            unitKey={item.unitKey}
            quota={limits[item.key]}
          />
        ))}
      </div>
    </div>
  );
};
